import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileSpreadsheet, FileText, RefreshCw, Loader2, Info, ShieldCheck, Download, Search,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const eur = (n) => `€${(Number(n) || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const YEARS = (() => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2];
})();

export default function EarningsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [year, setYear] = useState('');
  const [downloading, setDownloading] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('revenue_desc');
  const [page, setPage] = useState(1);
  const PER_PAGE = 15;

  const params = useMemo(() => {
    const p = {};
    if (start) p.start = start;
    if (end) p.end = end;
    if (year) p.year = year;
    return p;
  }, [start, end, year]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await api.get('/admin/earnings', { params });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the earnings report.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const download = async (path, key) => {
    setDownloading(key);
    setError('');
    try {
      const res = await api.get(path, { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : path.split('/').pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('That export could not be generated. There may be no verified deliveries in the selected period.');
    } finally {
      setDownloading('');
    }
  };

  const s = data?.summary;
  const allDrivers = data?.drivers || [];

  const SORTS = {
    revenue_desc: (a, b) => b.jd_revenue - a.jd_revenue,
    name_asc: (a, b) => a.driver_name.localeCompare(b.driver_name, 'en', { sensitivity: 'base' }),
    name_desc: (a, b) => b.driver_name.localeCompare(a.driver_name, 'en', { sensitivity: 'base' }),
    deliveries_desc: (a, b) => b.deliveries - a.deliveries,
    deliveries_asc: (a, b) => a.deliveries - b.deliveries,
    earner_desc: (a, b) => b.paid_to_drivers - a.paid_to_drivers,
  };

  const drivers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? allDrivers.filter((d) =>
        (d.driver_name || '').toLowerCase().includes(q) || (d.email || '').toLowerCase().includes(q))
      : allDrivers;
    return [...filtered].sort(SORTS[sort] || SORTS.revenue_desc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDrivers, search, sort]);

  useEffect(() => { setPage(1); }, [search, sort, data]);

  const totalPages = Math.max(1, Math.ceil(drivers.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageDrivers = drivers.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  return (
    <div className="space-y-6" data-testid="admin-earnings-panel">
      {/* Header + filters */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[#1B5E20]" />
            Only deliveries with a verified 6-digit code are counted.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="earnings-start" className="text-xs text-gray-500">From</Label>
            <Input id="earnings-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="h-9 w-[150px]" data-testid="earnings-start-date" />
          </div>
          <div>
            <Label htmlFor="earnings-end" className="text-xs text-gray-500">To</Label>
            <Input id="earnings-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="h-9 w-[150px]" data-testid="earnings-end-date" />
          </div>
          <div>
            <Label htmlFor="earnings-year" className="text-xs text-gray-500">Year</Label>
            <select id="earnings-year" value={year} onChange={(e) => setYear(e.target.value)}
              className="h-9 w-[110px] block border border-gray-200 rounded-md px-2 text-sm bg-white"
              data-testid="earnings-year-select">
              <option value="">All</option>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Button variant="outline" size="sm" className="h-9"
            onClick={() => { setStart(''); setEnd(''); setYear(''); }} data-testid="earnings-reset-btn">
            Reset
          </Button>
          <Button size="sm" className="h-9 bg-[#1B5E20] hover:bg-[#164a19]" onClick={load} data-testid="earnings-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {data?.period && (
        <p className="text-xs text-gray-500" data-testid="earnings-period-label">
          Showing <strong>{data.period.label}</strong>. Revenue reporting starts {data.revenue_epoch_label}.
        </p>
      )}

      {error && <p className="text-sm text-red-600" data-testid="earnings-error">{error}</p>}

      {loading || !s ? (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center" data-testid="earnings-loading">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Loading figures…</> : 'No figures to show.'}
        </div>
      ) : (
        <>
          {/* ───── Revenue summary ───── */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="earnings-summary">
            <SummaryCard label="Verified completed deliveries" value={s.deliveries} testid="sum-deliveries" />
            <SummaryCard label="Total delivery value" value={eur(s.delivery_value)} testid="sum-delivery-value" />
            <SummaryCard label="Booking deposits collected" value={eur(s.booking_deposits)} testid="sum-deposits" />
            <SummaryCard label="Driver fees collected" value={eur(s.driver_fees)} testid="sum-driver-fees" />
            <SummaryCard
              label="Total JustDeliver revenue"
              value={eur(s.jd_revenue)}
              hint={s.refunded > 0 ? `Net of ${eur(s.refunded)} refunded: ${eur(s.jd_revenue_net)}` : 'Deposits + driver fees'}
              tone="green"
              testid="sum-jd-revenue"
            />
            <SummaryCard
              label="Paid directly to drivers"
              value={eur(s.paid_to_drivers)}
              hint="Not JustDeliver revenue"
              tone="amber"
              testid="sum-paid-drivers"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <ExportButton label="Summary to Excel" icon={FileSpreadsheet} busy={downloading === 'sum-x'}
              onClick={() => download('/admin/earnings/export/summary.xlsx', 'sum-x')} testid="export-summary-excel" />
            <ExportButton label="Summary to PDF" icon={FileText} busy={downloading === 'sum-p'}
              onClick={() => download('/admin/earnings/export/summary.pdf', 'sum-p')} testid="export-summary-pdf" />
          </div>

          {/* ───── Driver earnings ───── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
            <h3 className="text-base font-bold text-gray-900">
              Driver earnings ({drivers.length}{drivers.length !== allDrivers.length ? ` of ${allDrivers.length}` : ''})
            </h3>
            <div className="flex flex-wrap gap-2">
              <ExportButton label="Export all to Excel" icon={FileSpreadsheet} busy={downloading === 'all-x'}
                onClick={() => download('/admin/earnings/export/drivers.xlsx', 'all-x')} testid="export-all-excel" />
              <ExportButton label="Export all to PDF" icon={FileText} busy={downloading === 'all-p'}
                onClick={() => download('/admin/earnings/export/drivers.pdf', 'all-p')} testid="export-all-pdf" />
            </div>
          </div>

          {/* Search + sort */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search driver by name or email…"
                className="h-10 pl-9"
                data-testid="earnings-driver-search"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-10 sm:w-[230px] border border-gray-200 rounded-md px-3 text-sm bg-white"
              data-testid="earnings-sort-select"
            >
              <option value="revenue_desc">JustDeliver revenue (highest)</option>
              <option value="name_asc">Name A–Z</option>
              <option value="name_desc">Name Z–A</option>
              <option value="deliveries_desc">Most deliveries</option>
              <option value="deliveries_asc">Fewest deliveries</option>
              <option value="earner_desc">Highest earning driver</option>
            </select>
          </div>

          {drivers.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-sm text-gray-500" data-testid="earnings-empty">
                <Info className="w-5 h-5 mx-auto mb-2 text-gray-400" />
                {search ? 'No drivers match your search.' : 'No verified completed deliveries in this period.'}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm" data-testid="earnings-table">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>Driver</Th>
                      <Th right>Verified jobs</Th>
                      <Th right>Delivery value</Th>
                      <Th right>Driver fees (10%)</Th>
                      <Th right>Booking deposits</Th>
                      <Th right>Paid to driver</Th>
                      <Th right>JustDeliver revenue</Th>
                      <Th>Exports</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageDrivers.map((d) => (
                      <tr key={d.driver_id} className="border-t border-gray-100 hover:bg-gray-50/70"
                        data-testid={`earnings-row-${d.driver_id}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-semibold text-gray-900">{d.driver_name}</p>
                          <p className="text-xs text-gray-500">{d.email || '—'}</p>
                        </td>
                        <Td right>{d.deliveries}</Td>
                        <Td right>{eur(d.delivery_value)}</Td>
                        <Td right>{eur(d.driver_fees)}</Td>
                        <Td right>{eur(d.booking_deposits)}</Td>
                        <Td right>
                          {eur(d.paid_to_drivers)}
                          {d.unrecorded_direct_payments > 0 && (
                            <span className="block text-[10px] text-amber-700">{d.unrecorded_direct_payments} not recorded</span>
                          )}
                        </Td>
                        <Td right><span className="font-bold text-[#1B5E20]">{eur(d.jd_revenue)}</span></Td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-1.5">
                            <IconExport title="Excel" icon={FileSpreadsheet} busy={downloading === `x-${d.driver_id}`}
                              onClick={() => download(`/admin/earnings/export/driver/${d.driver_id}.xlsx`, `x-${d.driver_id}`)}
                              testid={`export-driver-excel-${d.driver_id}`} />
                            <IconExport title="PDF" icon={FileText} busy={downloading === `p-${d.driver_id}`}
                              onClick={() => download(`/admin/earnings/export/driver/${d.driver_id}.pdf`, `p-${d.driver_id}`)}
                              testid={`export-driver-pdf-${d.driver_id}`} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden space-y-3">
                {pageDrivers.map((d) => (
                  <Card key={d.driver_id} className="border border-gray-200" data-testid={`earnings-card-${d.driver_id}`}>
                    <CardContent className="pt-4 space-y-2">
                      <div>
                        <p className="font-bold text-gray-900">{d.driver_name}</p>
                        <p className="text-xs text-gray-500">{d.email || '—'}</p>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                        <Pair k="Verified jobs" v={d.deliveries} />
                        <Pair k="Delivery value" v={eur(d.delivery_value)} />
                        <Pair k="Driver fees (10%)" v={eur(d.driver_fees)} />
                        <Pair k="Booking deposits" v={eur(d.booking_deposits)} />
                        <Pair k="Paid to driver" v={eur(d.paid_to_drivers)} />
                        <Pair k="JustDeliver revenue" v={eur(d.jd_revenue)} strong />
                      </dl>
                      <div className="flex gap-2 pt-1">
                        <ExportButton label="Excel" icon={FileSpreadsheet} busy={downloading === `x-${d.driver_id}`}
                          onClick={() => download(`/admin/earnings/export/driver/${d.driver_id}.xlsx`, `x-${d.driver_id}`)}
                          testid={`export-driver-excel-m-${d.driver_id}`} />
                        <ExportButton label="PDF" icon={FileText} busy={downloading === `p-${d.driver_id}`}
                          onClick={() => download(`/admin/earnings/export/driver/${d.driver_id}.pdf`, `p-${d.driver_id}`)}
                          testid={`export-driver-pdf-m-${d.driver_id}`} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 pt-1" data-testid="earnings-pagination">
                  <p className="text-xs text-gray-500">
                    Showing {(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, drivers.length)} of {drivers.length} drivers
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="h-9"
                      disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}
                      data-testid="earnings-prev-page">
                      <ChevronLeft className="w-4 h-4" /> Prev
                    </Button>
                    <span className="text-xs font-semibold text-gray-700" data-testid="earnings-page-indicator">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button variant="outline" size="sm" className="h-9"
                      disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}
                      data-testid="earnings-next-page">
                      Next <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, hint, tone, testid }) => {
  const tones = {
    green: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || 'border-gray-200 bg-white'}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 tabular-nums ${tone === 'green' ? 'text-[#1B5E20]' : tone === 'amber' ? 'text-amber-800' : 'text-gray-900'}`}>
        {value}
      </p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );
};

const ExportButton = ({ label, icon: Icon, onClick, busy, testid }) => (
  <Button variant="outline" size="sm" onClick={onClick} disabled={busy} data-testid={testid} className="h-9">
    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Icon className="w-3.5 h-3.5 mr-1.5" />}
    {label}
  </Button>
);

const IconExport = ({ title, icon: Icon, onClick, busy, testid }) => (
  <button type="button" title={title} onClick={onClick} disabled={busy} data-testid={testid}
    className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-[#1B5E20] transition-colors disabled:opacity-50">
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
  </button>
);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-xs font-semibold ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, right }) => (
  <td className={`px-3 py-2.5 tabular-nums ${right ? 'text-right' : ''}`}>{children}</td>
);
const Pair = ({ k, v, strong }) => (
  <>
    <dt className="text-gray-500">{k}</dt>
    <dd className={`text-right tabular-nums ${strong ? 'font-bold text-[#1B5E20]' : 'text-gray-900'}`}>{v}</dd>
  </>
);
